/**
 * Task #1451 — Gate "embedder-first".
 *
 * Mostrato come passo 1 del Routing AI quando il tenant non ha ancora scelto
 * l'embedder (`tenant_settings.embedder_provider IS NULL`). Spiega le due
 * opzioni e apre il popup di scelta (EmbedderChoiceWizard): la scelta
 * avviene sempre lì, con conferma esplicita e applicazione della
 * configurazione consigliata. La scelta è definitiva: cambiarla in seguito
 * impone la re-indicizzazione completa della KB.
 */
import { Lock, AlertCircle, Globe, Zap } from 'lucide-react';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';

export interface EmbedderGateOverlayProps {
  /** Apre il popup di scelta dell'embedder. */
  onChooseEmbedder: () => void;
}

export default function EmbedderGateOverlay({ onChooseEmbedder }: EmbedderGateOverlayProps) {
  return (
    <Card
      data-testid="embedder-gate-overlay"
      className="border-2 border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/20"
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 shrink-0">
            <Lock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-amber-900 dark:text-amber-100">
              Scegli l'embedder della Knowledge Base
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200 mt-1 leading-relaxed">
              Prima di configurare gli altri modelli devi scegliere l'embedder.
              È la base della pipeline RAG: tutti i suggerimenti delle funzioni
              dipendono da questa scelta.{' '}
              <strong>Decisione irreversibile</strong>: cambiarla in seguito
              impone re-indicizzazione completa della KB.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
          data-testid="embedder-gate-step1-cta"
        >
          <div
            className="text-left p-4 rounded-lg border-2 border-sky-200 dark:border-sky-800 bg-white dark:bg-gray-900"
            data-testid="embedder-gate-option-openai"
          >
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-sky-600" />
              <span className="font-semibold text-sky-900 dark:text-sky-100">OpenAI</span>
              <span className="text-[11px] text-gray-500 font-mono">text-embedding-3-large</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Solo testo · 1536 dim · maturo, qualità altissima sul retrieval
              testuale. Consigliato per KB documentali senza immagini, audio o video.
            </p>
          </div>

          <div
            className="text-left p-4 rounded-lg border-2 border-violet-200 dark:border-violet-800 bg-white dark:bg-gray-900"
            data-testid="embedder-gate-option-gemini"
          >
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4 text-violet-600" />
              <span className="font-semibold text-violet-900 dark:text-violet-100">Gemini</span>
              <span className="text-[11px] text-gray-500 font-mono">gemini-embedding-2</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Multimodale nativo · 3072 dim · cross-modal (testo ↔ immagini ↔
              audio ↔ video) nello stesso spazio semantico. Consigliato per KB
              con file misti.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button onClick={onChooseEmbedder} data-testid="embedder-gate-open-wizard">
            Scegli l'embedder
          </Button>
          <div className="flex items-start gap-2 text-[11px] text-amber-800 dark:text-amber-200">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Si apre la finestra di scelta con conferma: alla conferma viene
              applicata la configurazione consigliata per l'embedder scelto e le
              altre opzioni della pagina si sbloccano.
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
